/* The nest strip, composed in the browser: a port of nest_composer.py `render_layout`
 * (minicore/packs/printers/pack/_nest_src, the desktop's composeNest). The Helper takes one image per
 * command, so the web makes the strip here and prints it as one custom-size job — the same job the
 * desktop sends after composeNest.
 *
 *  - white strip, strip width × strip height at 300 dpi (lowered only when a browser canvas could
 *    not hold it);
 *  - boxes pasted in z order; each image turned 90° counter-clockwise when rotated (PIL rotate(90)),
 *    scaled to cover its box and centre-cropped (`fill`); parts off the strip are clipped and boxes
 *    entirely off it are skipped;
 *  - JPEG 95.
 */
import { pageExtraHeightFor } from './nestLayout.js';

const MAX_PIXELS = 150e6; // browsers refuse much larger canvases (Chrome ~268 M, Safari less)
const MAX_SIDE = 16000;

/** The DPI the strip is composed at: 300 unless that would not fit in a browser canvas. */
export function nestDpi(stripWidthIn, stripHeightIn, want = 300) {
  const byArea = Math.sqrt(MAX_PIXELS / Math.max(1e-6, stripWidthIn * stripHeightIn));
  const bySide = MAX_SIDE / Math.max(stripWidthIn, stripHeightIn);
  return Math.max(Math.min(60, want), Math.floor(Math.min(want, byArea, bySide)));
}

/** Decode `src` (Blob | URL) already scaled so the box crop needs no further resize. */
async function decodeFor(src, aspect, rotated, tw, th) {
  const blob = typeof src === 'string' ? await (await fetch(src)).blob() : src;
  // Full image size (source orientation) after the cover scale, from the aspect alone.
  let fw; let fh;
  if (!rotated) { fw = Math.max(tw, th / aspect); fh = fw * aspect; } else { const rw = Math.max(tw, th * aspect); fh = rw; fw = rw / aspect; }
  fw = Math.max(1, Math.round(fw)); fh = Math.max(1, Math.round(fh));
  try {
    return await createImageBitmap(blob, { resizeWidth: fw, resizeHeight: fh, resizeQuality: 'high' });
  } catch {
    return createImageBitmap(blob);
  }
}

/** Draw one box: cover-scale + centre crop, rotated 90° CCW when asked (nest_composer `fill`). */
function drawBox(g, bmp, rotated, ox, oy, tw, th) {
  const iw = bmp.width; const ih = bmp.height;
  g.fillStyle = '#ffffff';
  g.fillRect(ox, oy, tw, th);
  if (!rotated) {
    const s = Math.max(tw / iw, th / ih);
    const a = tw / s; const b = th / s;
    g.drawImage(bmp, (iw - a) / 2, (ih - b) / 2, a, b, ox, oy, tw, th);
    return;
  }
  // Rotated image R is ih wide × iw tall; R(x', y') = S(iw - y', x').
  const s = Math.max(tw / ih, th / iw);
  const a = tw / s; const b = th / s; // crop size in R
  const rx0 = (ih - a) / 2; const ry0 = (iw - b) / 2;
  g.save();
  g.translate(ox, oy + th);
  g.rotate(-Math.PI / 2);
  g.drawImage(bmp, iw - ry0 - b, rx0, b, a, 0, 0, th, tw);
  g.restore();
}

/**
 * @param {{family, stripWidthIn, stripHeightIn, dpi?, items:[{src, aspect, x, y, w, h, rotated}]}} spec
 *   items in paste (z) order; inches; src = File/Blob or a URL on brightivity.dev
 * @returns {Promise<{file: File, dpi, stripWidthIn, stripHeightIn, pageWidthIn, pageHeightIn, count}>}
 */
export async function composeNest({ family, stripWidthIn, stripHeightIn, dpi: wantDpi, items, quality = 0.95, name = 'nest.jpg' }) {
  const dpi = nestDpi(stripWidthIn, stripHeightIn, wantDpi || 300);
  const W = Math.round(stripWidthIn * dpi);
  const H = Math.round(stripHeightIn * dpi);
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, W, H);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  let count = 0;
  for (const it of items) {
    const tw = Math.round(it.w * dpi); const th = Math.round(it.h * dpi);
    const ox = Math.round(it.x * dpi); const oy = Math.round(it.y * dpi);
    if (ox >= W || oy >= H || ox + tw <= 0 || oy + th <= 0) continue; // entirely off the strip
    const bmp = await decodeFor(it.src, it.aspect, !!it.rotated, tw, th);
    drawBox(g, bmp, !!it.rotated, ox, oy, tw, th);
    bmp.close && bmp.close();
    count++;
  }
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  canvas.width = 1; canvas.height = 1; // free the pixels now
  if (!blob) throw new Error('This browser could not prepare the nest at that size.');
  return {
    file: new File([blob], name, { type: 'image/jpeg' }),
    dpi, count, stripWidthIn, stripHeightIn,
    pageWidthIn: stripWidthIn,
    pageHeightIn: Math.round((stripHeightIn + pageExtraHeightFor(family)) * 1000) / 1000,
  };
}

/** A small picture of the nest for the print toast (desktop nestThumbnail, 160 px). */
export async function nestThumbnail({ stripWidthIn, stripHeightIn, items }) {
  const px = 160;
  const dpi = px / Math.max(stripWidthIn, stripHeightIn || 1);
  const r = await composeNest({ family: null, stripWidthIn, stripHeightIn, dpi: Math.max(1, dpi), items, quality: 0.8, name: 'thumb.jpg' }).catch(() => null);
  return r ? URL.createObjectURL(r.file) : null;
}
